import { createContext, useState, useRef, useCallback } from "react";
import SeamService from "../services/SeamService";

export const SeamContext = createContext();

export const SeamProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [portalUrl, setPortalUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [resSyncLoading, setResSyncLoading] = useState(false);
  const [resSyncResult, setResSyncResult] = useState(null);
  const [timesSaving, setTimesSaving] = useState(false);
  const [timesSaved, setTimesSaved] = useState(false);

  // Debounce ref for reservation sync
  const resSyncInFlight = useRef(false);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await SeamService.getSettings();
      setSettings(data);
      return data;
    } catch (err) {
      console.error("Error loading Seam settings:", err);
      setError("Failed to load smart lock settings.");
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    try {
      setError(null);
      const { data } = await SeamService.updateSettings(updates);
      setSettings(data);
      return data;
    } catch (err) {
      console.error("Error updating Seam settings:", err);
      setError("Failed to update smart lock settings.");
      throw err;
    }
  };

  const createPortal = useCallback(async () => {
    try {
      setPortalLoading(true);
      setError(null);
      const { data } = await SeamService.createPortal();
      setPortalUrl(data.url);
      return data.url;
    } catch (err) {
      console.error("Error creating portal:", err);
      setError("Failed to load the smart lock management portal.");
    } finally {
      setPortalLoading(false);
    }
  }, []);

  const syncSpaces = async () => {
    try {
      setSyncLoading(true);
      setSyncResult(null);
      setError(null);
      const { data } = await SeamService.syncSpaces();
      setSyncResult(
        `Successfully synced ${data.synced} ${data.synced === 1 ? "property" : "properties"} to Seam.`
      );
      return data;
    } catch (err) {
      console.error("Error syncing spaces:", err);
      setError("Failed to sync properties. Please try again.");
    } finally {
      setSyncLoading(false);
    }
  };

  const syncReservations = async () => {
    // Debounce: skip if a sync is already in flight
    if (resSyncInFlight.current) return;
    resSyncInFlight.current = true;

    try {
      setResSyncLoading(true);
      setResSyncResult(null);
      setError(null);
      const { data } = await SeamService.syncReservations();
      const parts = [];
      if (data.synced > 0) {
        parts.push(`Synced ${data.synced} ${data.synced === 1 ? "reservation" : "reservations"} across ${data.mapped_properties} ${data.mapped_properties === 1 ? "property" : "properties"} with mapped locks.`);
      }
      if (data.deactivated > 0) {
        parts.push(`Deactivated codes for ${data.deactivated} cancelled/deleted ${data.deactivated === 1 ? "reservation" : "reservations"}.`);
      }
      if (parts.length === 0) {
        setResSyncResult(data.message || "No reservations to sync.");
      } else {
        setResSyncResult(parts.join(" "));
      }
      return data;
    } catch (err) {
      console.error("Error syncing reservations:", err);
      setError(
        err.response?.data?.message ||
          "Failed to sync reservations. Please try again."
      );
    } finally {
      setResSyncLoading(false);
      resSyncInFlight.current = false;
    }
  };

  const saveTimes = async (checkInTime, checkOutTime) => {
    try {
      setTimesSaving(true);
      setTimesSaved(false);
      setError(null);
      const { data } = await SeamService.updateSettings({
        standard_check_in_time: checkInTime,
        standard_check_out_time: checkOutTime,
      });
      setSettings(data);
      setTimesSaved(true);
      setTimeout(() => setTimesSaved(false), 3000);
      return data;
    } catch (err) {
      console.error("Error saving times:", err);
      setError("Failed to save check-in/out times.");
    } finally {
      setTimesSaving(false);
    }
  };

  const seamCtx = {
    settings,
    portalUrl,
    loading,
    portalLoading,
    syncLoading,
    error,
    syncResult,
    resSyncLoading,
    resSyncResult,
    timesSaving,
    timesSaved,
    setError,
    setPortalUrl,
    setSyncResult,
    setResSyncResult,
    loadSettings,
    updateSettings,
    createPortal,
    syncSpaces,
    syncReservations,
    saveTimes,
  };

  return (
    <SeamContext.Provider value={seamCtx}>{children}</SeamContext.Provider>
  );
};
